import createDebugLogger from 'debug';
import {toQueries} from '../candidate-search-utils.js';
import {getMelindaIdsF035, validateSidFieldSubfieldCounts, getSubfieldValues, testStringOrNumber} from '../../matching-utils.js';
import {extractHostMelindaIdsFromField, extractPublicationYearFrom773, getHostMelindaFields} from './component.js';
import {fieldToString} from '@natlibfi/marc-record-validators-melinda';
import {getTitle} from '../../match-detection/features/bib/title.js';

const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query');

const IS_OK_ALONE_THRESHOLD = 5;

export function bibSourceIds(record) {

  /* Melinda's SRU-index melinda.sourceid includes source IDs from SID fields in Melinda records
     SID-fields in Melinda have sf $c with local id and sf $b with a code for the local db:

     SID__ $c 123457 $b helka
     SID__ $c (ANDL100020)1077305 $b sata
     SID__ $c VER999999 $ FI-KV
     SID__ $c /10024/508126 $ REPO_THESEUS

    In melinda.sourceid -index case is kept, sourceprefixes in brackets and hyphens are normalized away:
    Note: slashes are not normalized away, but a SRU-search-string including slashes needs to be quoted

    1234567helka
    1077305sata
    VER999999FIKV
    /10024/508126REPO_THESEUS

    Note: All Melinda records that have a matching records in a local db do NOT have SID for that local records,
          existence of a SID field depends on how the record has been added to Melinda and how it has been handled
          afterwards. SIDs are also not reliably maintained. A record might or might not have a SID for a local db
          after the matching record is removed from the local db.

   */


  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:source-ids');
  const debugData = debug.extend('data');
  //const debugInfo = debug.extend('info');

  debug(`Creating queries for sourceid's`);

  const fSids = record.get('SID');
  debugData(`SID-fields (${fSids.length}): ${JSON.stringify(fSids)}`);

  return fSids.length > 0 ? toSidQueries(fSids) : [];

  function toSidQueries(fSids) {
    debug(`Creating actual queries for sourceid's`);

    const sidStrings = getSidStrings(fSids);

    if (sidStrings.length < 1) {
      debug(`No identifiers found, no queries created.`);
      return [];
    }

    const sidQueries = toQueries(sidStrings, 'melinda.sourceid');

    return sidQueries;

    function getSidStrings(fSids) {
      debug(`Getting Sid strings from SID fields`);

      // Map SID fields to valid sidStrings, filter out empty strings
      const sidStrings = fSids.map(toSidString).filter(nonEmptySid => nonEmptySid);
      return sidStrings;

      function toSidString(field) {
        debug(`Getting string from a field`);

        return validateSidFieldSubfieldCounts(field) ? createSidString(field) : '';

        function createSidString(field) {
          debug(`Creating string from a field`);
          const [sfC] = getSubfieldValues(field, 'c');
          const [sfB] = getSubfieldValues(field, 'b');

          const cleanedSfC = removeSourcePrefix(normalizeSidSubfieldValue(sfC));
          const cleanedSfB = normalizeSidSubfieldValue(sfB);

          debugData(`${JSON.stringify(sfC)} + ${JSON.stringify(sfB)}`);
          return cleanedSfC.concat(cleanedSfB);
        }

        function removeSourcePrefix(subfieldValue) {
          const sourcePrefixRegex = (/^(?<sourcePrefix>\([A-Za-z0-9-]+\))(?<id>.+)$/u);
          const normalizedValue = testStringOrNumber(subfieldValue) ? String(subfieldValue).replace(sourcePrefixRegex, '$<id>') : '';
          debugData(`Normalized ${subfieldValue} to ${normalizedValue}`);
          return normalizedValue;
        }

        function normalizeSidSubfieldValue(subfieldValue) {
          debugData(`Normalizing ${subfieldValue}`);
          const normalizeAwayRegex = (/[- ]/u);
          return testStringOrNumber(subfieldValue) ? String(subfieldValue).replace(normalizeAwayRegex, '') : '';
        }

      }
    }
  }
}

export function bibMelindaIds(record) {
  // Melinda's SRU-index melinda.melindaid includes f001 controlnumbers and old Melinda-IDs from f035z's for all non-deleted Melinda-records

  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibMelindaIds');
  const debugData = debug.extend('data');
  debug(`Creating queries for MelindaIds`);

  // Note: Melinda-ID's for search queries are created just from records f035a's and f035z's
  // Both (FI-MELINDA)- and FCC-prefixed forms are found
  // f001 controlnumber is not currently included, even if record's f003 is FI-MELINDA
  const melindaIds = getMelindaIdsF035(record);

  debugData(`Unique identifiers (${melindaIds.length}): ${JSON.stringify(melindaIds)}`);

  if (melindaIds.length < 1) {
    debug(`No identifiers found, no queries created.`);
    return [];
  }

  return toQueries(melindaIds, 'melinda.melindaid');
}


// bibHostComponents returns host id from the first subfield $w of first field f773, see test-fixtures 04 and 05
// bibHostComponents should search all 773 $ws for possible host id, but what should it do in case of multiple host ids?
export function bibHostComponents(record) {
  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibHostComponents');
  const debugData = debug.extend('data');
  debug(`Creating queries for hostIds`);
  const f773s = getHostMelindaFields(record);
  if (f773s.length !== 1) { // This needs some thinking...
    return [];
  }

  const [id] = extractHostMelindaIdsFromField(f773s[0]);
  debug(`Found id: ${JSON.stringify(id)}`);

  // NB! record.isCR() does not work if LDR/07=a, so we get year from 773$g!
  const year = extractPublicationYearFrom773(f773s[0]);
  debug(`${fieldToString(f773s[0])} => ${year ? year : 'N/A'}`);
  if (year) {
    return [`dc.date=${year} AND melinda.partsofhost=${id}`];
  }

  return [`melinda.partsofhost=${id}`];

}




// SRU search dc.title with a search phrase starting with ^ maps currently in Melinda to (probably) to *headings* index TIT
// - Aleph cannot currently handle headings searches starting with a boolean - in these cases use word search

export function bibTitle(record) {
  // We get author/publisher only when formatted title is shorter than 5 chars
  return bibTitleAuthorPublisher({record, onlyTitleLength: 5});
}

export function bibTitleAuthor(record) {
  debug('bibTitleAuthor');
  // We use onlyTitleLength that is longer than our formatted length to
  // get an author or an publisher always
  return bibTitleAuthorPublisher({record, onlyTitleLength: 100});
}

export function bibTitleAuthorYear(record) {
  debug('bibTitleAuthorYearAlternates');
  // We use onlyTitleLength that is longer than our formatted length to
  // get an author or an publisher always

  return bibTitleAuthorPublisher({record, onlyTitleLength: 100, addYear: true});
}

export function bibTitleAuthorYearAlternates(record) {
  debug('bibTitleAuthorYearAlternates');
  // We use onlyTitleLength that is longer than our formatted length to
  // get an author or an publisher always
  const origQueryList = bibTitleAuthorPublisher({record, onlyTitleLength: 100, addYear: true, alternates: true, alternateQueries: []});
  debug(`${JSON.stringify(origQueryList)}`);
  return {queryList: Array.from(origQueryList).reverse(), queryListType: 'alternates'};
}

function getTitleForQuery(record) {
  const title = getTitle(record, ['a']);
  if (title) {
    const nWords = title.split(' ');
    // If lone $a is deemed too short, fetch $b as well:
    // (NV: I've seen pairs with 245$a-only vs 245$a$b, so I'd like to use $a only if it is long enough)

    if (nWords < 3 || title.length < 12) {
      const [f245] = record.get('245');
      // If punctuation is ' =' I think that f245$a is good enough despite shortness. Trying to balance between two bad situations...
      // "Suo (= short title) siellä, vetelä (= missing $b name in another language) täällä..."
      if (f245 && f245.subfields.find(sf => sf.code === 'a' && sf.value.match(/ =$/u)) && title.length >= IS_OK_ALONE_THRESHOLD) {
        return title;
      }
      return getTitle(record, ['a', 'b']); // Try to get a longer title
    }
  }
  return title;
}

function dcTitle(record, onlyTitleLength, alternates = false) {
  const title = getTitleForQuery(record);
  if (!testStringOrNumber(title)) {
    return [];
  }

  const formatted = getFormattedTitle(title);

  // use word search for titles starting with a boolean
  const useWordSearch = checkUseWordSearch(formatted);
  // Prevent too many matches / SRU crashing by having a minimum length
  // Note that currently this fails matching if there are no matches from previous matchers
  if (formatted.length >= onlyTitleLength && !alternates) {
    return [`dc.title="${useWordSearch ? '' : '^'}${formatted}*"`, formatted, true];
  }

  const queryIsOkAlone = !useWordSearch && formatted.length >= IS_OK_ALONE_THRESHOLD;

  // use word search without ending * also in combination searches to avoid SRU-server crashes [MRA-189]
  return [`dc.title="${useWordSearch || !queryIsOkAlone ? '' : '^'}${formatted}${queryIsOkAlone ? '*' : ''}"`, formatted, queryIsOkAlone];

  function getFormattedTitle(title) {
    const formatted = String(title)
      .replace(/[\-+ !"(){}\[\]<>;:.?/@*%=^_`~]/gu, ' ')
      .replace(/[^\w\s\p{Alphabetic}]/gu, '') // Apparently matches to/works with non-aplhabetic scripts such as Chinese as well
      .replace(/  +/gu, ' ') // Clean up concurrent spaces from eg. subfield changes
      .trim()
      .replace(/^(.{30}\S*) .*$/, "$1")
      .trim();

    return formatted;
  }
}

export function bibTitleAuthorPublisher({record, onlyTitleLength, addYear = false, alternates = false, alternateQueries = []}) {
  debug(`bibTitleAuthorPublisher, onlyTitleLength: ${onlyTitleLength}, addYear: ${addYear}, alternates: ${alternates}`);
  const [query, formatted, queryIsOkAlone] = dcTitle(record, onlyTitleLength, alternates);
  if (query === undefined) {
    return [];
  }

  debug(`query: ${query}`);

  // Prevent too many matches / SRU crashing by having a minimum length
  // Note that currently this fails matching if there are no matches from previous matchers
  if (formatted.length >= onlyTitleLength && !alternates) {
    return [query];
  }

  const newAlternateQueries = alternates ? [...alternateQueries, query] : alternateQueries;

  return addAuthorsToSearch({query, queryIsOkAlone, addYear, alternates, alternateQueries: newAlternateQueries});

  function addAuthorsToSearch({query, queryIsOkAlone = false, addYear = false, alternates = false, alternateQueries = []}) {
    debug('addAuthorsToSearch');
    const [authorQuery] = bibAuthors(record);
    if (authorQuery !== undefined) {
      if (addYear) {
        const newAlternateQueries = alternates ? [...alternateQueries, `${authorQuery} AND ${query}`] : alternateQueries;
        return addYearToSearch({query: `${authorQuery} AND ${query}`, queryIsOkAlone: true, alternates, alternateQueries: newAlternateQueries});
      }
      return alternates ? alternateQueries : [`${authorQuery} AND ${query}`];
    }
    return addPublisherToSearch({query, queryIsOkAlone, addYear, alternates, alternateQueries});
    //return [];
  }

  function addPublisherToSearch({query, queryIsOkAlone = false, addYear = false, alternates = false, alternateQueries = []}) {
    const [publisherQuery] = bibPublishers(record);
    if (publisherQuery !== undefined) {
      if (addYear) {
        const newAlternateQueries = alternates ? [...alternateQueries, `${publisherQuery} AND ${query}`] : alternateQueries;
        return addYearToSearch({query: `${publisherQuery} AND ${query}`, queryIsOkAlone: true, alternates, alternateQueries: newAlternateQueries});
      }
      return alternates ? alternateQueries : [`${publisherQuery} AND ${query}`];
    }
    if (queryIsOkAlone && !addYear) {
      return alternates ? alternateQueries : [`${query}`];
    }
    return addYearToSearch({query, queryIsOkAlone, alternates, alternateQueries});
  }

  function addYearToSearch({query, queryIsOkAlone = false, alternates = false, alternateQueries = []}) {
    const [yearQuery] = bibYear(record);
    if (yearQuery !== undefined) {
      const yearAndOtherQueries = `${yearQuery} AND ${query}`;
      return alternates ? [...alternateQueries, yearAndOtherQueries] : [yearAndOtherQueries];
    }
    if (queryIsOkAlone) {
      return alternates ? alternateQueries : [`${query}`];
    }
    return [];
  }

}

export function bibAuthors(record) {
  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibAuthors');
  const debugData = debug.extend('data');
  debug(`Creating query for the first author`);
  //debugData(record);

  const author = getAuthor(record);

  if (testStringOrNumber(author)) {
    const formatted = String(author)
      .replace(/[^\w\s\p{Alphabetic}]/gu, '')
      // Clean up concurrent spaces from fe. subfield changes
      .replace(/ +/gu, ' ')
      .trim()
      .slice(0, 30)
      .trim();

    // use word search for authors starting with a boolean
    const useWordSearch = checkUseWordSearch(formatted);
    // Prevent too many matches by having a minimum length
    debugData(`Author string: ${formatted}`);
    if (formatted.length >= 5) {
      return [`dc.author="${useWordSearch ? '' : '^'}${formatted}*"`];
    }
    return [];
  }

  return [];

  function getAuthor(record) {
    //debugData(record);
    const [field] = record.get(/^(?:100|110|111|700|710|711)$/u);
    //debugData(field);

    if (field) {
      const authorString = field.subfields
        .filter(({code}) => ['a'].includes(code)) // We might use different subfield code sets for X00, X10 and X11?
        .map(({value}) => testStringOrNumber(value) ? String(value) : '')
        .filter(value => value)
        // In Melinda's index subfield separators are indexed as ' '
        .join(' ');
      return authorString;
    }
    return false;
  }
}

export function bibPublishers(record) {
  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibPublishers');
  const debugData = debug.extend('data');
  debug(`Creating query for the first publisher`);
  //debugData(record);

  const publisher = getPublisher(record);
  if (testStringOrNumber(publisher)) {
    const formatted = String(publisher)
      .replace(/[^\w\s\p{Alphabetic}]/gu, '')
      // Clean up concurrent spaces from fe. subfield changes
      .replace(/ +/gu, ' ')
      .trim()
      .slice(0, 30)
      .trim();

    debugData(`Publisher string: '${formatted}'`);
    if (formatted.length === 0) {
      return [];
    }
    // use non-wildcard word search from dc.publisher
    return [`dc.publisher="${formatted}"`];
  }

  return [];

  function getPublisher(record) {
    //debugData(record);
    const [field] = record.get(/^26[04]$/u).filter(f => f.subfields.some(sf => sf.code === 'b')); // Filter removes copyright-only fields
    //debugData(field);

    if (field) {
      const publisherString = field.subfields
        .filter(({code}) => ['b'].includes(code))
        .map(({value}) => testStringOrNumber(value) ? String(value) : '')
        .filter(value => value)
        // In Melinda's index subfield separators are indexed as ' '
        .join(' ')
        .trim();
      return publisherString;
    }
    return false;
  }
}

export function bibYear(record) {
  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibYear');
  const debugData = debug.extend('data');
  debug(`Creating query for the publishing year`);

  const year = getYear(record);
  if (year) {
    return [`dc.date="${year}"`];
  }
  return [];

  function getYear(record) {
    const [f008] = record.get(/^008$/u);
    if (!f008 || !f008.value || f008.value.length < 11) {
      debug('f008 missing/crappy');
      return false;
    }

    debugData(`f008: ${JSON.stringify(f008)}`);
    const {value} = f008;
    if (value === '||||' || value === '    ') { // Meaningless values
      return false;
    }
    return value.slice(7, 11);
  }
}

export function checkUseWordSearch(formatted) {
  const lowercased = formatted.toLowerCase();
  // Note: add a space to startWords to catch just actual boolean words
  const booleanStartWords = ['and ', 'or ', 'nor ', 'not '];
  return booleanStartWords.some(word => lowercased.startsWith(word));
}

export function bibStandardIdentifiers(record) {

  const debug = createDebugLogger('@natlibfi/melinda-record-matching:candidate-search:query:bibStandardIdentifiers');
  const debugData = debug.extend('data');
  debug(`Creating queries for standard identifiers`);

  // DEVELOP: should we query also f015 and f028?

  // const fields = record.get(/^(?<def>020|022|024)$/u);
  const fields = record.get(/^(?<def>015|020|022|024|028)$/u);
  const identifiers = [].concat(...fields.map(toIdentifiers));
  const uniqueIdentifiers = [...new Set(identifiers)];

  debugData(`Standard identifier fields: ${JSON.stringify(fields)}`);
  debugData(`Identifiers (${identifiers.length}): ${JSON.stringify(identifiers)}`);
  debugData(`Unique identifiers (${uniqueIdentifiers.length}): ${JSON.stringify(uniqueIdentifiers)}`);

  if (uniqueIdentifiers.length < 1) {
    debug(`No identifiers found, no queries created.`);
    return [];
  }

  return toQueries(uniqueIdentifiers, 'dc.identifier');

  function toIdentifiers({tag, subfields}) {
    const issnIsbnReqExp = (/^[A-Za-z0-9-]+$/u);
    const otherIdReqExp = (/^[A-Za-z0-9-:]+$/u);

    if (tag === '015') { // TODO: test (does this use the right index etc.)
      return subfields
        .filter(sf => sf.code === 'a' && sf.value.match(/^[A-Z0-9\-]+$/ui))
        .map(sf => sf.value)
    }

    if (tag === '020') {
      return subfields
        .filter(sub => ['a', 'z'].includes(sub.code) && testStringOrNumber(sub.value) && issnIsbnReqExp.test(String(sub.value)))
        .map(({value}) => String(value));
    }

    if (tag === '022') {
      return subfields
        .filter(sub => ['a', 'z', 'y'].includes(sub.code) && testStringOrNumber(sub.value) && issnIsbnReqExp.test(String(sub.value)))
        .map(({value}) => String(value));
    }

    if (tag === '028') { // TODO: test
      const a = subfields.find(sf => sf.code === 'a');
      if (a) {
        const b = subfields.find(sf => sf.code === 'b');
        // TODO: normalize?
        if (b) {
          return [`^${a.value}`, `^${b.value} ${a.value}`];
        }
        return [`^${a.value}`];
      }
    }

    // Default seems to be 024:
    return subfields
      .filter(sub => ['a', 'z'].includes(sub.code) && testStringOrNumber(sub.value) && otherIdReqExp.test(String(sub.value)))
      .map(({value}) => String(value));
  }
}
